<template>
  <div>
    <staff-vacation-request-button
      v-if="isStaff"
      :staff-id="staffId"
      :year="year"
      :leave-type="leaveType"
      @update-table="reFetchList"
    />

    <div
      v-if="vacationList.length"
      class="table-responsive"
    >
      <span
        v-if="submitting"
        class="spinner-border spinner-border-sm"
        role="status"
        aria-hidden="true"
      />
      <table class="table table-striped table-bordered">
        <thead>
          <tr>
            <th>No</th>
            <th>Start Date</th>
            <th>End Date</th>
            <th>Type</th>
            <th>Reason</th>
            <th>Days</th>
            <th>Status</th>
            <th v-if="isStaff">
              Amend
            </th>
            <th v-if="isStaff">
              Nudge
            </th>
            <th v-if="isManager" />
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(vacation, key) in vacationList"
            :key="vacation.id"
          >
            <td>
              {{ ++key }}
            </td>
            <td>{{ vacation.start_date }}</td>
            <td>{{ vacation.end_date }}</td>
            <td>{{ showType(vacation) }}</td>
            <td>{{ vacation.reason }}</td>
            <td>{{ vacation.total_days }}</td>
            <td>{{ getStatus(vacation) }}</td>
            <td v-if="isStaff">
              <!-- group of buttons -->
              <button
                type="button"
                class="btn btn-success"
                :disabled="isSubmitted(vacation) || vacation.is_rejected || submitting"
                @click="submitRequest(vacation.id)"
              >
                Submit
              </button>
              <button
                type="button"
                class="btn btn-warning"
                :disabled="canCancel(vacation) || vacation.is_rejected || submitting"
                @click="cancelRequest(vacation.id)"
              >
                Cancel Request
              </button>
            </td>
            <td v-if="isStaff">
              <button
                type="button"
                class="btn btn-info"
                :disabled="vacation.is_approved || !vacation.submitted || submitting || reminding"
                @click="submitRequest(vacation.id, 'remind')"
              >
                Remind
              </button>
            </td>
            <td v-if="isManager">
              <button
                type="button"
                class="btn btn-success"
                :disabled="vacation.is_approved || submitting "
                @click="approveRequest(vacation)"
              >
                Approve
              </button>
              <button
                type="button"
                class="btn btn-danger"
                :disabled="vacation.is_rejected || submitting"
                @click="rejectRequest(vacation)"
              >
                Reject
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div v-else>
      <p>No Vacation Request Existed</p>
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";

export default {
    name: "VacationListTable",
    props: {
        vacationList: {
            type: Array,
            required: true,
        },
        year: {
            type: Number,
            required: true,
        },
        staffId: {
            type: Number,
            required: true,
        },
        isManager: {
            type: Boolean,
            required: true,
        },
        leaveType: {
            type: String,
            required: false,
            default: "vacation",
        },
    },
    data() {
        return {
            submitting: false,
            reminding: false,
        };
    },
    computed: {
        isStaff() {
            return !this.isManager;
        },
    },
    methods: {
        isApproved(vacation) {
            return vacation.is_approved === 1;
        },
        isSubmitted(vacation) {
            return vacation.submitted === 1;
        },
        canCancel(vacation) {
            return vacation.is_approved === 1;
        },
        getStatus(vacation) {
            if (this.isApproved(vacation)) {
                return "Approved";
            } else if (vacation.is_rejected) {
                return "Rejected";
            } else {
                if (vacation.submitted) return "Submitted";
                else return "Draft";
            }
        },
        showType(vacation) {
            return vacation.sick_leave === 1 ? "Sick Leave" : "Vacation";
        },
        reFetchList() {
            this.$emit("get-list", this.leaveType);
        },
        async approveRequest(vacation) {
            try {
                if (confirm("Do you want to approve this request?") === false) {
                    return;
                }

                this.submitting = true;
                await this.managerUpdateRequest({ id: vacation.id, status: "approved", type: "vacation" });
                alert("Leave Request Approved");
                this.$emit("get-list", this.leaveType);
            } catch (error) {
                alert("Error Occured");
                console.log(error);
            } finally {
                this.submitting = false;
            }
        },
        async rejectRequest(vacation) {
            try {
                if (confirm("Do you want to reject this request?") === false) {
                    return;
                }

                this.submitting = true;
                await this.managerUpdateRequest({ id: vacation.id, status: "rejected", type: "vacation" });
                alert("Vacation Request Rejected");
                this.$emit("get-list", this.leaveType);
            } catch (error) {
                alert("Error Occured");
                console.log(error);
            } finally {
                this.submitting = false;
            }
        },
        async submitRequest(vacationId, type = "submit") {
            try {
                let confirmText = "submit";
                if (type === "remind") {
                    confirmText = "remind";
                }

                if (confirm(`Do you want to ${confirmText} this request?`) === false) {
                    return;
                }

                if (type === "remind") {
                    this.reminding = true;
                } else {
                    this.submitting = true;
                }

                await this.submitVacationRequest({ id: vacationId, type: type });

                let message = "Leave Request Submitted";
                if (type === "remind") {
                    message = "Leave Request Reminded";
                }
                alert(message);
                this.$emit("get-list", this.leaveType);
            } catch (error) {
                alert("Error Occured");
                console.log(error);
            } finally {
                this.submitting = false;
                this.reminding = false;
            }
        },
        async cancelRequest(vacationId) {
            try {
                this.submitting = true;
                await this.cancelVacationRequest({ id: vacationId });
                alert("Vacation Request Cancelled");
                this.$emit("get-list", this.leaveType);
            } catch (error) {
                alert("Error Occured");
                console.log(error);
            } finally {
                this.submitting = false;
            }
        },
        ...mapActions({
            cancelVacationRequest: "vacation/cancelRequest",
            submitVacationRequest: "vacation/submitRequest",
            getVacationList: "vacation/getStaffVacations",
            managerUpdateRequest: "vacation/managerUpdateRequest",
        }),
    },
};
</script>

